/* ============================================================================
   haiku-tint — the page takes its colour from the photograph you are looking at

   Four slides, four photographs. As each comes to the middle of the screen the
   page background drifts toward a colour drawn from it, and back out again on
   the way past. Nothing else moves.

   WHY HUE ONLY. A dominant-colour average of a photograph is a real colour —
   and putting a real colour behind a page whose whole language is near-white
   would wreck it. So the photograph contributes its HUE, and a little of its
   saturation; the lightness is pinned to the page's own. Measured base is
   #eaeeed = hsl(165, 10.5%, 92.5%), so every tint here is the same paper at a
   different temperature, never a colour.

   WHY NOT A FLAT AVERAGE. Averaging every pixel of a photograph of a black
   t-shirt gives dark grey, whose hue is noise. Pixels that are nearly black,
   nearly white or nearly grey carry no usable hue, so they are discarded and
   the rest are averaged AROUND THE WHEEL (hues are angles: the mean of 350 deg
   and 10 deg is 0, not 180). Too few usable pixels and the slide simply
   contributes nothing.

   WHY THE TOKEN AND NOT A BACKDROP. Writing --color-background means the blur
   band, the sticky bar and every panel that samples the page colour follow it.
   Painting a separate layer behind everything would leave those pieces still
   mixing the OLD colour, which reads as a bug rather than a mood.

   LIGHT MODE ONLY. The dark palette is a set of tinted blacks chosen by hand;
   drifting it from photographs would fight that, not serve it.

   FAIL-SAFE. Sampling can fail — a cross-origin image, a canvas the browser
   refuses to read. Any slide that cannot be read is skipped, and if none can
   be read the page keeps its own colour and nothing is written at all.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuTintConfig || {};
  if (cfg.enabled === false) return;

  var SAT_MIN = typeof cfg.satMin === 'number' ? cfg.satMin : 6;   // % of the output
  var SAT_MAX = typeof cfg.satMax === 'number' ? cfg.satMax : 14;
  var STRENGTH = typeof cfg.strength === 'number' ? cfg.strength : 1;

  function darkMode() {
    var m = document.documentElement.getAttribute('data-mode');
    if (m === 'dark') return true;
    if (m === 'light') return false;
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  }

  /* ---- colour helpers ---------------------------------------------------- */

  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2, d = max - min;
    if (d) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
    }
    return [h, s * 100, l * 100];
  }

  function hslToRgb(h, s, l) {
    h = ((h % 360) + 360) % 360; s /= 100; l /= 100;
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    var m = l - c / 2, r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }
    return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
  }

  // Hues are angles; average them as unit vectors or 350 and 10 average to 180.
  function meanHue(list) {
    var x = 0, y = 0;
    for (var i = 0; i < list.length; i++) {
      var a = list[i].h * Math.PI / 180, w = list[i].w;
      x += Math.cos(a) * w; y += Math.sin(a) * w;
    }
    if (!x && !y) return null;
    return Math.atan2(y, x) * 180 / Math.PI;
  }

  function lerpHue(a, b, t) {
    var d = ((b - a + 540) % 360) - 180;   // shortest way round
    return a + d * t;
  }

  /* ---- sampling ---------------------------------------------------------- */

  function sample(url) {
    return new Promise(function (res) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () {
        try {
          var N = 16, cv = document.createElement('canvas');
          cv.width = N; cv.height = N;
          var cx = cv.getContext('2d', { willReadFrequently: true });
          cx.drawImage(im, 0, 0, N, N);
          var d = cx.getImageData(0, 0, N, N).data, usable = [], satSum = 0;
          for (var i = 0; i < d.length; i += 4) {
            var hsl = rgbToHsl(d[i], d[i + 1], d[i + 2]);
            // No usable hue in near-black, near-white or near-grey.
            if (hsl[1] < 15 || hsl[2] < 12 || hsl[2] > 90) continue;
            usable.push({ h: hsl[0], w: hsl[1] / 100 });   // weight by saturation
            satSum += hsl[1];
          }
          if (usable.length < 8) { res(null); return; }
          var h = meanHue(usable);
          if (h === null) { res(null); return; }
          res({ h: h, s: satSum / usable.length });
        } catch (e) { res(null); }
      };
      im.onerror = function () { res(null); };
      im.src = url;
    });
  }

  /* ---- the run ----------------------------------------------------------- */

  function boot() {
    /* DARK MODE IS DECIDED CONTINUOUSLY, NOT ONCE.

       This checked darkMode() at boot and never again. On the homepage the
       tint had already written --color-background by then, so switching to
       dark left the light tint pinned on :root and the page stayed off-white
       — measured rgb(243,236,229) in every mode, while a product page (no
       tint) went to rgb(10,10,10) correctly. The toggle was never at fault.

       The observer releases the variable the moment the mode changes and puts
       it back if light returns, so the two features stop fighting over the
       same token. */
    var root0 = document.documentElement;
    new MutationObserver(function () {
      if (darkMode()) {
        root0.style.removeProperty('--color-background-rgb');
        root0.style.removeProperty('--color-background');
      } else if (window.haikuTint && window.haikuTint.refresh) {
        window.haikuTint.refresh();
      }
    }).observe(root0, { attributes: true, attributeFilter: ['data-mode'] });

    if (darkMode()) return;

    var root = document.documentElement;
    var slides = Array.prototype.slice.call(
      document.querySelectorAll('.mount-vertical-slideshow .slide')
    ).filter(function (s) { return s.querySelector('img'); });
    if (slides.length < 2) return;

    // The page's own colour is the anchor everything returns to.
    var cs = getComputedStyle(root);
    var baseRgbStr = (cs.getPropertyValue('--color-background-rgb') || '234, 238, 237').trim();
    var parts = baseRgbStr.split(',').map(function (n) { return parseInt(n, 10); });
    if (parts.length !== 3 || parts.some(isNaN)) return;
    var baseHsl = rgbToHsl(parts[0], parts[1], parts[2]);
    var BASE = { h: baseHsl[0], s: baseHsl[1], l: baseHsl[2] };

    var stops = [];   // { el, h, s }

    Promise.all(slides.map(function (slide) {
      var img = slide.querySelector('.slide-image--primary img') || slide.querySelector('img');
      var url = img && (img.currentSrc || img.src);
      if (!url) return Promise.resolve(null);
      return sample(url).then(function (c) {
        return c ? { el: slide, h: c.h, s: c.s } : null;
      });
    })).then(function (found) {
      stops = found.filter(Boolean);
      if (!stops.length) return;     // nothing readable: leave the page alone
      start();
    });

    function toneOf(stop) {
      // The photograph's saturation decides how far from grey this tint sits,
      // inside a band that can never become a colour.
      var s = Math.max(SAT_MIN, Math.min(SAT_MAX, (stop.s / 100) * SAT_MAX * 1.6)) * STRENGTH;
      return { h: stop.h, s: s };
    }

    function apply(h, s) {
      var rgb = hslToRgb(h, s, BASE.l);
      root.style.setProperty('--color-background-rgb', rgb.join(', '));
      root.style.setProperty('--color-background',
        'rgb(' + rgb[0] + ', ' + rgb[1] + ', ' + rgb[2] + ')');
    }

    function clear() {
      root.style.removeProperty('--color-background-rgb');
      root.style.removeProperty('--color-background');
    }

    function frame() {
      pending = false;
      /* A scroll that lands while dark must not re-write the token the
         observer just cleared. */
      if (darkMode()) { clear(); return; }
      var mid = window.innerHeight / 2;

      /* Each slide's influence is how close its middle is to the screen's
         middle, falling to nothing a screen away. Blending them all rather
         than picking a nearest pair is what makes the change continuous
         instead of stepping at the boundaries. */
      var wsum = 0, parts2 = [], satAcc = 0;
      for (var i = 0; i < stops.length; i++) {
        var r = stops[i].el.getBoundingClientRect();
        var d = Math.abs(r.top + r.height / 2 - mid);
        var reach = window.innerHeight * 0.9 + r.height / 2;
        var w = 1 - Math.min(1, d / reach);
        if (w <= 0) continue;
        w = w * w;                                  // ease
        var t = toneOf(stops[i]);
        parts2.push({ h: t.h, w: w });
        satAcc += t.s * w;
        wsum += w;
      }

      if (!wsum) { apply(BASE.h, BASE.s); return; }

      var h = meanHue(parts2);
      if (h === null) { apply(BASE.h, BASE.s); return; }
      var s = satAcc / wsum;

      // Fade the whole effect in and out against the page's own colour, so the
      // top and bottom of the page are never tinted.
      var k = Math.min(1, wsum);
      apply(lerpHue(BASE.h, h, k), BASE.s + (s - BASE.s) * k);
    }

    var pending = false;
    function onScroll() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(frame);
    }

    function start() {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      frame();
      window.haikuTint = { clear: clear, stops: stops.length, refresh: frame };
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}());
